import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useSetupAuthenticatorMutation, useIsAuthenticatorSetupQuery } from './authenticatorApi';
import { Shield, Smartphone, Copy, AlertTriangle, CheckCircle, QrCode } from 'lucide-react';

export const Authenticator = () => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [setupAuthenticator, { data: setupData, isLoading: isSettingUp }] = useSetupAuthenticatorMutation();
  const { data: isSetupData, isLoading: isCheckingSetup, refetch } = useIsAuthenticatorSetupQuery();

  const isAlreadySetup = isSetupData?.isSetup;
  const qrCode = setupData?.data?.qr;
  const secretKey = setupData?.data?.base32;

  const handleSetupClick = async () => {
    if (isAlreadySetup) setConfirmDialogOpen(true);
    else await handleProceed();
  };

  const handleProceed = async () => {
    try {
      await setupAuthenticator().unwrap();
      refetch();
      setConfirmDialogOpen(false);
    } catch (err) {
      console.error('Setup failed:', err);
      setConfirmDialogOpen(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  if (isCheckingSetup) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4'></div>
          <p className='text-slate-400'>Loading authenticator status...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className='mb-8'>
        <h2 className='text-3xl font-bold text-white mb-2'>Two-Factor Authentication</h2>
        <p className='text-slate-400'>Log-in into any Hainok account by setting up your 2Factor Auth.</p>
      </div>

      {/* Status Card */}
      <Card className='bg-slate-800 border-slate-700 mb-6'>
        <CardHeader>
          <CardTitle className='text-white flex items-center gap-3'>
            <Shield className='h-6 w-6' />
            Authenticator Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              {isAlreadySetup ? (
                <>
                  <CheckCircle className='h-5 w-5 text-green-500' />
                  <div>
                    <p className='text-white font-medium'>Authenticator Enabled</p>
                    <p className='text-sm text-slate-400'>You can now log in securely</p>
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className='h-5 w-5 text-yellow-500' />
                  <div>
                    <p className='text-white font-medium'>Authenticator Not Configured</p>
                    <p className='text-sm text-slate-400'>Set up 2FA to start using authenticator</p>
                  </div>
                </>
              )}
            </div>
            <Badge
              variant={isAlreadySetup ? 'default' : 'secondary'}
              className={`${isAlreadySetup ? 'bg-green-500 text-black' : 'bg-yellow-500 text-black'}`}
            >
              {isAlreadySetup ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className='grid gap-6 lg:grid-cols-2'>
        {/* Setup Card */}
        <Card className='bg-slate-800 border-slate-700'>
          <CardHeader>
            <CardTitle className='text-white flex items-center gap-3'>
              <Smartphone className='h-5 w-5' />
              {isAlreadySetup ? 'Reconfigure Authenticator' : 'Setup Authenticator'}
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='text-slate-300'>
              {isAlreadySetup ? (
                <p>
                  Your authenticator is already configured. You can reconfigure it with a new device if needed.
                  <span className='text-yellow-400 block mt-2 text-sm'>
                    ⚠️ This will unlink your previously configured authenticator app.
                  </span>
                </p>
              ) : (
                <p>
                  Set up two-factor authentication using an authenticator app like Google Authenticator, Authy, or any
                  compatible TOTP app.
                </p>
              )}
            </div>
            <Button
              onClick={handleSetupClick}
              disabled={isSettingUp}
              className='w-full'
              variant={isAlreadySetup ? 'destructive' : 'default'}
            >
              {isSettingUp ? 'Setting up...' : isAlreadySetup ? 'Reconfigure Authenticator' : 'Setup Authenticator'}
            </Button>
          </CardContent>
        </Card>

        {/* QR Code Card - Only show when setup data is available */}
        {qrCode && (
          <Card className='bg-slate-800 border-slate-700'>
            <CardHeader>
              <CardTitle className='text-white flex items-center gap-3'>
                <QrCode className='h-5 w-5' />
                Scan QR Code
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='text-slate-300 text-sm mb-4'>
                Scan this QR code with your authenticator app or enter the secret key manually.
              </div>

              {/* QR Code */}
              <div className='flex justify-center p-4 bg-white rounded-lg'>
                <img src={qrCode} alt='QR Code for 2FA setup' className='max-w-full h-auto' />
              </div>

              {/* Secret Key */}
              {secretKey && (
                <div className='space-y-2'>
                  <p className='text-sm font-medium text-slate-300'>Manual Entry Key:</p>
                  <div className='flex items-center gap-2 p-3 bg-slate-700 rounded-lg'>
                    <code className='flex-1 text-sm text-white font-mono break-all'>{secretKey}</code>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => copyToClipboard(secretKey)}
                      className='flex-shrink-0'
                    >
                      {copySuccess ? <CheckCircle className='h-4 w-4 text-green-500' /> : <Copy className='h-4 w-4' />}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions Card */}
        {!qrCode && (
          <Card className='bg-slate-800 border-slate-700'>
            <CardHeader>
              <CardTitle className='text-white'>Setup Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-4 text-slate-300'>
                <div className='space-y-2'>
                  <p className='font-medium text-white'>How to set up 2FA:</p>
                  <ol className='list-decimal list-inside space-y-1 text-sm'>
                    <li>Download an authenticator app (Google Authenticator, Authy, etc.)</li>
                    <li>Click "Setup Authenticator" to generate a QR code</li>
                    <li>Scan the QR code with your authenticator app</li>
                    <li>Enter the 6-digit code from the app as the password of the user you intend to login to</li>
                  </ol>
                </div>

                <div className='pt-4 border-t border-slate-700'>
                  <p className='font-medium text-white mb-2'>Recommended Apps:</p>
                  <ul className='text-sm space-y-1'>
                    <li>• Google Authenticator</li>
                    <li>• Microsoft Authenticator</li>
                    <li>• Authy</li>
                    <li>• 1Password</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className='bg-slate-800 border-slate-700 text-white'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-yellow-400'>
              <AlertTriangle className='h-5 w-5' />
              Reconfigure Authenticator
            </DialogTitle>
            <DialogDescription className='text-slate-400'>
              This will unlink your previously configured authenticator app. You'll need to set up 2FA again with your
              authenticator app using the new QR code.
            </DialogDescription>
          </DialogHeader>
          <div className='flex gap-3 justify-end mt-6'>
            <Button variant='outline' onClick={() => setConfirmDialogOpen(false)} disabled={isSettingUp}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleProceed} disabled={isSettingUp}>
              {isSettingUp ? 'Proceeding...' : 'Proceed'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
